import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import _get from 'lodash/get';
import { useLocalStorage } from 'usehooks-ts';
import toast from '@lib/components/Toast/Toast';
import { GetUrlParams } from '@lib/enums/urls';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import useSearchParam from '@lib/hooks/useSearchParam';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import createSearchFilters from '@lib/utils/createSearchFilters';
import { USER_DOCUMENT_LIST_FILTER_FORM } from '@lib/enums/localStorageKeys';
import { Value } from '@lib/interfaces/form';
import { GetLoadOptions } from '@lib/components/Select/types';
import {
  Documentation,
  DocumentationFilterInputObject,
  DocumentationScopeNameEnum,
  useAssetsSearchListLazyQuery,
  useEdmDocumentationCategoriesSearchListLazyQuery,
  useSectorsSearchListLazyQuery,
  useSiteAreasSearchListLazyQuery,
  useSitesSearchListLazyQuery,
  FilterGroupingEnum,
} from 'graphql-common';
import {
  EdmDocumentationsQueryVariables,
  useEdmDocumentationBulkFavoriteMutation,
  useEdmDocumentationBulkUnfavoriteMutation,
  useEdmDocumentationsLightQuery,
} from '@lib/graphql-common';
import { getDocumentationCategoriesLoadOptions as getDocumentationCategoriesLoadOptionsFn } from 'utils/fetch/searchLists/documentationCategoriesSearchList';
import { getSectorsLoadOptions as getSectorsLoadOptionsFn } from 'utils/fetch/searchLists/sectorsSearchList';
import { getSitesLoadOptions as getSitesLoadOptionsFn } from 'utils/fetch/searchLists/sitesSearchList';
import { getSiteAreasLoadOptions as getSiteAreasLoadOptionsFn } from 'utils/fetch/searchLists/siteAreasSearchList';
import { getAssetsLoadOptions as getAssetsLoadOptionsFn } from 'utils/fetch/searchLists/assetsSearchList';
import { APP_URLS } from 'constants/urls';
import { DocumentTabEnum } from '../enums';
import useDocumentFilterFields, {
  DocumentFilterFieldNames,
  DOCUMENT_FILTER_EMPTY_VALUES,
} from './useDocumentFilterFields';

type SelectOption = { value: string; label: string };

interface UseDocumentsActionsProps {
  tabParam: string;
  skip?: boolean;
}

// Operational user platform shows only documents linked to a task / form
// template / form question / asset the current user can access — never the
// full company list. The container short-circuits to a lock empty state on
// Free plan (mirrors `InterventionsContainer`), so this hook always runs in
// paid-plan mode.
function getScope(tabParam: string) {
  switch (tabParam) {
    case DocumentTabEnum.Favorites:
      return { name: DocumentationScopeNameEnum.LinkedToAccessibleFavorited };
    case DocumentTabEnum.Unread:
      return { name: DocumentationScopeNameEnum.LinkedToAccessibleUnread };
    default:
      return { name: DocumentationScopeNameEnum.LinkedToAccessible };
  }
}

function getIds(filterParams: { [key: string]: Value }, name: string) {
  return ((_get(filterParams, name, []) as SelectOption[]) || []).map(
    (o) => o.value,
  );
}

function getLabels(filterParams: { [key: string]: Value }, name: string) {
  return ((_get(filterParams, name, []) as SelectOption[]) || []).map(
    (o) => o.label,
  );
}

export default function useDocumentsActions({
  tabParam,
  skip = false,
}: UseDocumentsActionsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery] = useSearchParam();
  const [paginationParams] = usePaginationParams();
  const [filterParams, setFilterParams] = useLocalStorage<{
    [key: string]: Value;
  }>(
    USER_DOCUMENT_LIST_FILTER_FORM,
    DOCUMENT_FILTER_EMPTY_VALUES as { [key: string]: Value },
  );

  // Search list lazy queries
  const [fetchCategories] = useEdmDocumentationCategoriesSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [fetchSectors] = useSectorsSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [fetchSites] = useSitesSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [fetchAreas] = useSiteAreasSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [fetchAssets] = useAssetsSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  const getCategoriesLoadOptions: GetLoadOptions = () =>
    getDocumentationCategoriesLoadOptionsFn(fetchCategories);
  const getSectorsLoadOptions: GetLoadOptions = () =>
    getSectorsLoadOptionsFn(fetchSectors);
  const getSitesLoadOptions: GetLoadOptions = () =>
    getSitesLoadOptionsFn(fetchSites, { includeSector: true });
  const getAreasLoadOptions: GetLoadOptions = () =>
    getSiteAreasLoadOptionsFn(fetchAreas);
  const getEquipmentLoadOptions: GetLoadOptions = () =>
    getAssetsLoadOptionsFn(fetchAssets);

  // Filter fields
  const filterFields = useDocumentFilterFields({
    getCategoriesLoadOptions,
    getSectorsLoadOptions,
    getSitesLoadOptions,
    getAreasLoadOptions,
    getEquipmentLoadOptions,
    t,
  });

  // Filter counter
  const filterCounter = useMemo(() => {
    let count = 0;
    Object.values(DocumentFilterFieldNames).forEach((fieldName) => {
      if (_get(filterParams, [fieldName, 'length'])) {
        count += 1;
      }
    });
    return count;
  }, [filterParams]);

  // Build query variables
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    paginationParams,
    GetUrlParams.OrderingDirection,
  );

  const scope = getScope(tabParam);

  const queryVariables: EdmDocumentationsQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
    scope,
  };

  if (orderingField && orderingDirection) {
    queryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }

  const filters: DocumentationFilterInputObject[] = [];
  if (searchQuery) {
    filters.push(
      ...createSearchFilters(searchQuery, [
        { fieldName: 'name' },
        { fieldName: 'code' },
        { fieldName: 'categoryName' },
        { fieldName: 'tagsName' },
      ]),
    );
  }

  // BE exposes categoryName (not categoryId); filter by selected option label
  const categoryNames = getLabels(
    filterParams,
    DocumentFilterFieldNames.Categories,
  );
  const sectorIds = getIds(filterParams, DocumentFilterFieldNames.Sectors);
  const siteIds = getIds(filterParams, DocumentFilterFieldNames.Sites);
  const areaIds = getIds(filterParams, DocumentFilterFieldNames.Areas);
  const equipmentIds = getIds(filterParams, DocumentFilterFieldNames.Equipment);

  if (categoryNames.length) {
    filters.push({
      categoryName: {
        grouping: FilterGroupingEnum.And,
        predicate: { in: categoryNames },
      },
    });
  }
  if (sectorIds.length) {
    filters.push({
      sectorsId: {
        grouping: FilterGroupingEnum.And,
        predicate: { in: sectorIds },
      },
    });
  }
  if (siteIds.length) {
    filters.push({
      sitesId: {
        grouping: FilterGroupingEnum.And,
        predicate: { in: siteIds },
      },
    });
  }
  if (areaIds.length) {
    filters.push({
      linkedAreasId: {
        grouping: FilterGroupingEnum.And,
        predicate: { in: areaIds },
      },
    });
  }
  if (equipmentIds.length) {
    filters.push({
      linkedAssetsId: {
        grouping: FilterGroupingEnum.And,
        predicate: { in: equipmentIds },
      },
    });
  }

  if (filters.length) {
    queryVariables.filters = filters;
  }

  // Main list query
  const mainQuery = useEdmDocumentationsLightQuery({
    fetchPolicy: 'cache-and-network',
    variables: queryVariables,
    skip,
  });

  const { loading, data, previousData } = mainQuery;
  const queryData = data || previousData;
  const firstLoading = loading && previousData === undefined;
  const collection = _get(queryData, 'data.collection', []) as Documentation[];
  const totalCount = _get(queryData, 'data.metadata.totalCount', 0) as number;

  // Tab counter queries
  const counterFilters = filters.length ? filters : undefined;

  const allCountQuery = useEdmDocumentationsLightQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      limit: 1,
      filters: counterFilters,
      scope: getScope(DocumentTabEnum.All),
    },
    skip,
  });
  const allCount = allCountQuery.data?.data?.metadata?.totalCount ?? 0;

  const favoritesCountQuery = useEdmDocumentationsLightQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      limit: 1,
      filters: counterFilters,
      scope: getScope(DocumentTabEnum.Favorites),
    },
    skip,
  });
  const favoritesCount =
    favoritesCountQuery.data?.data?.metadata?.totalCount ?? 0;

  const unreadCountQuery = useEdmDocumentationsLightQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      limit: 1,
      filters: counterFilters,
      scope: getScope(DocumentTabEnum.Unread),
    },
    skip,
  });
  const unreadCount = unreadCountQuery.data?.data?.metadata?.totalCount ?? 0;

  // Favorites mutations
  const [bulkFavorite, { loading: favoriteLoading }] =
    useEdmDocumentationBulkFavoriteMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });
  const [bulkUnfavorite, { loading: unfavoriteLoading }] =
    useEdmDocumentationBulkUnfavoriteMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });
  const isFavoriteLoading = favoriteLoading || unfavoriteLoading;

  const onToggleFavorite = useCallback(
    (item: Documentation) => {
      const isFavorited = item.favorited;
      const mutation = isFavorited ? bulkUnfavorite : bulkFavorite;
      const successMessage = isFavorited
        ? t('remove-from-favorites-success')
        : t('add-to-favorites-success');
      mutation({
        variables: { ids: [item.id] },
        update: (cache) => {
          cache.modify({
            id: cache.identify(item),
            fields: {
              favorited: () => !isFavorited,
            },
          });
        },
        onCompleted: () => {
          toast({ content: successMessage });
          favoritesCountQuery.refetch();
          if (tabParam === DocumentTabEnum.Favorites) {
            mainQuery.refetch();
          }
        },
      });
    },
    [bulkFavorite, bulkUnfavorite, favoritesCountQuery, mainQuery, t, tabParam],
  );

  // Modal state
  const [fileViewerDocument, setFileViewerDocument] =
    useState<Documentation | null>(null);
  const [versionNotesDocument, setVersionNotesDocument] =
    useState<Documentation | null>(null);

  const onViewDocument = useCallback((item: Documentation) => {
    setFileViewerDocument(item);
  }, []);

  const closeFileViewer = useCallback(() => {
    setFileViewerDocument(null);
  }, []);

  const onVersionNotes = useCallback((item: Documentation) => {
    setVersionNotesDocument(item);
  }, []);

  const closeVersionNotes = useCallback(() => {
    setVersionNotesDocument(null);
  }, []);

  const onDetails = useCallback(
    (item: Documentation) => {
      navigate(
        APP_URLS.app.documentation.view.getDynamicPath({
          pathParts: { id: item.id },
        }),
      );
    },
    [navigate],
  );

  return {
    allCount,
    documentationsError: mainQuery.error,
    closeFileViewer,
    closeVersionNotes,
    collection,
    favoritesCount,
    fileViewerDocument,
    filterCounter,
    filterFields,
    filterParams,
    firstLoading,
    isFavoriteLoading,
    loading,
    onDetails,
    onToggleFavorite,
    onVersionNotes,
    onViewDocument,
    searchQuery,
    setFilterParams,
    totalCount,
    unreadCount,
    versionNotesDocument,
  };
}
